<script setup>
// HelpBob: editable code field with live CSS syntax highlighting.
// Technique: a highlight.js layer rendered behind a transparent <textarea>,
// kept in sync on scroll. Uses the already-bundled highlight.js (no new dep).
import 'highlight.js/styles/github.css';
import 'highlight.js/lib/common';
import { ref } from 'vue';

defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue']);

// ref on the wrapping DOM <div> (NOT the highlightjs component instance),
// so we can drive its scroll position to mirror the textarea.
const preWrapRef = ref(null);

const onInput = e => emit('update:modelValue', e.target.value);
const syncScroll = e => {
  if (!preWrapRef.value) return;
  preWrapRef.value.scrollTop = e.target.scrollTop;
  preWrapRef.value.scrollLeft = e.target.scrollLeft;
};
</script>

<template>
  <div class="hb-css-editor">
    <!-- highlighted background layer (scrolled programmatically) -->
    <div ref="preWrapRef" class="hb-css-editor__pre" aria-hidden="true">
      <highlightjs language="css" :code="modelValue || ' '" />
    </div>
    <!-- transparent editable layer on top (owns the scrollbar) -->
    <textarea
      :value="modelValue"
      :placeholder="placeholder"
      spellcheck="false"
      autocapitalize="off"
      autocomplete="off"
      autocorrect="off"
      class="hb-css-editor__input"
      @input="onInput"
      @scroll="syncScroll"
    />
  </div>
</template>

<style scoped>
.hb-css-editor {
  position: relative;
  width: 100%;
  height: 22rem;
  min-height: 12rem;
  resize: vertical;
  overflow: hidden;
  border: 1px solid rgb(var(--color-slate-400) / 0.4);
  border-radius: 0.5rem;
  background: #ffffff;
}

/* Both layers MUST share identical text metrics + padding so the caret
   aligns with the highlighted glyphs and wrapping/height match exactly. */
.hb-css-editor__pre,
.hb-css-editor__input,
.hb-css-editor__pre :deep(pre),
.hb-css-editor__pre :deep(code) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px;
  line-height: 1.6;
  letter-spacing: 0;
  tab-size: 2;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  word-break: break-word;
}

.hb-css-editor__pre,
.hb-css-editor__input {
  position: absolute;
  inset: 0;
  margin: 0;
  width: 100%;
  height: 100%;
  padding: 0.75rem;
  border: 0;
  box-sizing: border-box;
}

/* background highlight layer: clipped, scrolled via JS, non-interactive */
.hb-css-editor__pre {
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}
/* highlightjs renders <pre><code>; neutralise its own box so it lays out
   identically to the textarea content. */
.hb-css-editor__pre :deep(pre),
.hb-css-editor__pre :deep(code),
.hb-css-editor__pre :deep(.hljs) {
  margin: 0 !important;
  padding: 0 !important;
  background: transparent !important;
  border: 0 !important;
  overflow: visible !important;
  display: block;
}

/* editable layer on top: owns the scrollbar, transparent text + visible caret */
.hb-css-editor__input {
  z-index: 1;
  resize: none;
  overflow: auto;
  background: transparent;
  color: transparent;
  caret-color: #1a1714;
  outline: none;
}
.hb-css-editor__input::placeholder {
  color: rgb(var(--color-slate-400));
}
</style>
